import { Auth, Connect, Message, SocketObservable } from '@genesislcap/foundation-comms';
import { DI } from '@microsoft/fast-foundation';
import { IWatchlist, Project } from '../types';
import { CASCADE_COMPANY_ID_WATCHLIST, WATCHLIST_ENTITY_TYPE } from '../utils';

export interface IProjectService {
  getAllProjects(CRITERIA_MATCH: string): SocketObservable<Message>;
  getAllDraftProjects(CRITERIA_MATCH: string): SocketObservable<Message>;
  getAllWatchlistProjects(CRITERIA_MATCH: string): SocketObservable<Message>;
  getAllWatchlistVPPAs(CRITERIA_MATCH: string): SocketObservable<Message>;
  sendProjectCsv(file: any, endpointUrl: any): Promise<any>;
  eventProjectsInsert(projectsList: Array<Project>): Promise<any>;
  eventInsertFavoritedProject(EntityId: string): Promise<any>;
  eventDeleteFavoritedProject(EntityId: string): Promise<any>;
  listUnmaskedProjectsForBuyerCompany(sellerCompanyId: string, buyerCompanyId: string): Promise<any>;
  getWatchlistProject(data): Promise<Message<any>>;
  getProjectDocumentStatus(data): SocketObservable<Message<any>>;
  getProjectScore(projectId: string): Promise<Message<any>>;
  getProjectKeyMetrics(projectId: string): Promise<Message<any>>;
  getProjectMetrics(projectId: string): Promise<Message<any>>;
  getProjectRangeDate(projectId: string): Promise<Message<any>>;
}

class ProjectServiceImpl implements IProjectService {
  constructor(
    @Connect private connect: Connect,
    @Auth private auth: Auth,
  ) {}

  async getWatchlistProject(data): Promise<Message<any>> {
    return this.connect.request('WATCHLIST', {
      REQUEST: {
        WATCHLIST_ENTITY_ID: data,
      },
    });
  }

  getProjectDocumentStatus(projectId: string): SocketObservable<Message<any>> {
    const request: any = this.connect.stream(
        'PROJECT_DOCUMENT_STATUS',
        (streamMessage: Message) => {
          () => {};
        },
        (x) => {},
        { CRITERIA_MATCH: `PROJECT_ID == '${projectId}'` },
    );

    return request;
  }


  getAllProjects(CRITERIA_MATCH: string): SocketObservable<Message<any>> {
    const params = {
      CRITERIA_MATCH: CRITERIA_MATCH,
    };
    const request: any = this.connect.stream(
      'ALL_PROJECTS',
      (streamMessage: Message) => {
        () => {};
      },
      (x) => {},
      params,
    );

    return request;
  }

  getAllDraftProjects(CRITERIA_MATCH: string): SocketObservable<Message<any>> {
    const params = {
      CRITERIA_MATCH,
    };
    const request: any = this.connect.stream(
      'ALL_DRAFT_PROJECTS',
      (streamMessage: Message) => {
        () => {};
      },
      (x) => {},
      params,
    );

    return request;
  }

  getAllWatchlistProjects(CRITERIA_MATCH: string): SocketObservable<Message<any>> {
    const params = {
      CRITERIA_MATCH,
    };
    const request: any = this.connect.stream(
      'WATCHLIST_PROJECTS',
      (streamMessage: Message) => {
        () => {};
      },
      (x) => {},
      params,
    );

    return request;
  }

  getAllWatchlistVPPAs(CRITERIA_MATCH: string): SocketObservable<Message<any>> {
    const params = {
      CRITERIA_MATCH,
    };
    const request: any = this.connect.stream(
      'WATCHLIST_VPPAS',
      (streamMessage: Message) => {
        () => {};
      },
      (x) => {},
      params,
    );

    return request;
  }

  sendProjectCsv(file: any, endpointUrl: any): Promise<any> {
    const headers = new Headers();
    headers.append('SESSION_AUTH_TOKEN', localStorage.getItem('authToken'));

    const formData = new FormData();
    formData.append(file.fileName, file);

    const settings = {
      method: 'POST',
      body: formData,
      headers: headers,
    };

    return new Promise((resolve, reject) => {
      try {
        resolve(
          fetch(endpointUrl, settings).then((fetchResponse) => {
            return fetchResponse.json();
          }),
        );
      } catch (error) {
        reject(error);
      }
    });
  }

  public async eventProjectsInsert(projectsList: Array<Project>): Promise<any> {
    if (!projectsList) return;

    const request: any = await this.connect.commitEvent('EVENT_PROJECTS_INSERT', {
      DETAILS: { PROJECTS_LIST: projectsList },
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });

    return request;
  }
  public async eventInsertFavoritedProject(EntityId: string): Promise<any> {
    if (!EntityId) return;
    const watchlist: IWatchlist = {
      CASCADE_COMPANY_ID: CASCADE_COMPANY_ID_WATCHLIST.CASCADE_COMPANY_ID,
      WATCHLIST_ENTITY_ID: EntityId,
      WATCHLIST_ENTITY_ID_TYPE: WATCHLIST_ENTITY_TYPE.PROJECT,
    };

    const request: any = await this.connect.commitEvent('EVENT_WATCHLIST_INSERT', {
      DETAILS: watchlist,
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });
    return request;
  }

  public async eventDeleteFavoritedProject(EntityId: string): Promise<any> {
    if (!EntityId) return;
    const watchlist: IWatchlist = {
      CASCADE_COMPANY_ID: CASCADE_COMPANY_ID_WATCHLIST.CASCADE_COMPANY_ID,
      WATCHLIST_ENTITY_ID: EntityId,
      WATCHLIST_ENTITY_ID_TYPE: WATCHLIST_ENTITY_TYPE.PROJECT,
    };

    const request: any = await this.connect.commitEvent('EVENT_WATCHLIST_DELETE', {
      DETAILS: watchlist,
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });
    return request;
  }

  getProjectScore(projectId: string): Promise<Message<any>> {
    return this.connect.request('REQ_PROJECT_SCORE_CALCULATION', {
      REQUEST: {
        PROJECT_ID: projectId,
      },
    });
  }

  getProjectKeyMetrics(projectId: string): Promise<Message<any>> {
    return this.connect.request('REQ_PROJECT_KEY_METRICS', {
      REQUEST: {
        PROJECT_ID: projectId,
      },
    });
  }


  public listUnmaskedProjectsForBuyerCompany(
    sellerCompanyId: string,
    buyerCompanyId: string

  ): Promise<Message<any>> {
    return this.connect.request("LIST_UNMASKED_PROJECTS", {
        REQUEST: {
            SELLER_COMPANY_ID: sellerCompanyId,
            BUYER_COMPANY_ID: buyerCompanyId,
        },
    });
  }

  getProjectMetrics(projectId: string): Promise<Message<any>> {
    return this.connect.request('REQ_PROJECT_METRICS', {
      REQUEST: {
        PROJECT_ID: projectId,
      },
    });
  }

  getProjectRangeDate(projectId: string): Promise<Message<any>> {
    return this.connect.request('PROJECT_RANGE_DATE', {
      REQUEST: {
        PROJECT_ID: projectId,
      },
    });
  }
}

export const IProjectService = DI.createInterface<IProjectService>((x) =>
  x.singleton(ProjectServiceImpl),
);
